/**
 * EPL Field Designer - Interface visual para crear campos de formulario
 * 
 * Funcionalidades:
 * - Crear campos: text, textarea, select, radio, checkbox
 * - Configurar validaciones: required, min/max length, patterns
 * - Preview en tiempo real
 * - Drag & Drop para reordenar
 * - Duplicar y eliminar campos
 */

import React, { useState, useCallback } from 'react';
import { 
  Plus, 
  Trash2, 
  Copy, 
  GripVertical, 
  Type, 
  AlignLeft, 
  List, 
  CheckCircle2,
  Square,
  Eye,
  Settings,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

const FIELD_TYPES = [
  { 
    id: 'text', 
    name: 'Texto Corto', 
    icon: Type,
    description: 'Campo de texto de una línea',
    premium: false
  },
  { 
    id: 'textarea', 
    name: 'Texto Largo', 
    icon: AlignLeft,
    description: 'Campo de texto multilínea',
    premium: false
  },
  { 
    id: 'select', 
    name: 'Selección', 
    icon: List,
    description: 'Lista desplegable con opciones',
    premium: false
  },
  { 
    id: 'radio', 
    name: 'Opción Única', 
    icon: CheckCircle2,
    description: 'Botones de radio (una opción)',
    premium: false
  },
  { 
    id: 'checkbox', 
    name: 'Múltiple', 
    icon: Square,
    description: 'Casillas de verificación (varias opciones)',
    premium: false
  },
  // 🌟 PREMIUM FIELDS
  { 
    id: 'matrix', 
    name: 'Matriz', 
    icon: CheckCircle2,
    description: 'Evaluación bidimensional (criterios vs categorías)',
    premium: true
  },
  { 
    id: 'table', 
    name: 'Tabla Estructurada', 
    icon: List,
    description: 'Registro de información en filas y columnas',
    premium: true
  },
  { 
    id: 'ranking', 
    name: 'Ranking', 
    icon: AlignLeft,
    description: 'Ordenamiento por prioridad o preferencia',
    premium: true
  },
  { 
    id: 'section_step', 
    name: 'Sección / Paso', 
    icon: Square,
    description: 'Agrupa campos en etapas secuenciales',
    premium: true
  }
];

const DEFAULT_FIELD = {
  id: '',
  type: 'text',
  label: '',
  placeholder: '',
  description: '',
  required: false,
  validation: {
    minLength: null,
    maxLength: null,
    pattern: '',
    customMessage: ''
  },
  options: [],
  defaultValue: '',
  premiumConfig: {
    // Matrix
    rows: 3,
    columns: 3,
    rowLabels: [],
    columnLabels: [],
    cellType: 'text',
    allowEmpty: false,
    // Table
    tableColumns: [],
    maxRows: 10,
    minRows: 1,
    rowAddable: true,
    // Ranking
    items: [],
    maxItems: 10,
    allowTies: false,
    // Section
    sectionTitle: '',
    sectionDescription: '',
    allowCollapse: true,
    showNumber: true,
    childFields: []
  }
};

const EplFieldDesigner = ({ fields = [], onChange, preview = false }) => {
  const [selectedField, setSelectedField] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Generar UUID v4 válido
  const generateUuid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  };

  const generateId = () => generateUuid();

  const addField = useCallback((type) => {
    const newField = {
      ...DEFAULT_FIELD,
      id: generateId(),
      type,
      label: `${FIELD_TYPES.find(ft => ft.id === type)?.name || 'Campo'} ${fields.length + 1}`,
      options: ['select', 'radio', 'checkbox'].includes(type) ? ['Opción 1', 'Opción 2'] : []
    };

    const updatedFields = [...fields, newField];
    onChange(updatedFields);
    setSelectedField(newField.id);
  }, [fields, onChange]);

  const updateField = useCallback((fieldId, updates) => {
    const updatedFields = fields.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    );
    onChange(updatedFields);
  }, [fields, onChange]);

  const removeField = useCallback((fieldId) => {
    const updatedFields = fields.filter(field => field.id !== fieldId);
    onChange(updatedFields);
    if (selectedField === fieldId) {
      setSelectedField(null);
    }
  }, [fields, onChange, selectedField]);

  const duplicateField = useCallback((fieldId) => {
    const fieldToDuplicate = fields.find(field => field.id === fieldId);
    if (fieldToDuplicate) {
      const duplicated = {
        ...fieldToDuplicate,
        id: generateId(),
        label: `${fieldToDuplicate.label} (copia)`
      };
      const updatedFields = [...fields, duplicated];
      onChange(updatedFields);
    }
  }, [fields, onChange]);

  const moveField = useCallback((fieldId, direction) => {
    const currentIndex = fields.findIndex(field => field.id === fieldId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= fields.length) return;

    const updatedFields = [...fields];
    [updatedFields[currentIndex], updatedFields[newIndex]] = 
    [updatedFields[newIndex], updatedFields[currentIndex]];
    
    onChange(updatedFields);
  }, [fields, onChange]);

  const selectedFieldData = fields.find(field => field.id === selectedField);

  if (preview) {
    return <FieldPreview fields={fields} />;
  }

  return (
    <div className="flex h-full bg-gray-50">
      {/* Panel izquierdo - Lista de campos */}
      <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Campos del Formulario</h3>
          
          {/* Botones para agregar campos */}
          <div className="grid grid-cols-1 gap-2 mb-4">
            {FIELD_TYPES.map((fieldType) => {
              const IconComponent = fieldType.icon;
              return (
                <button
                  key={fieldType.id}
                  onClick={() => addField(fieldType.id)}
                  className={`flex items-center gap-3 p-3 text-left border rounded-lg transition-colors relative ${
                    fieldType.premium 
                      ? 'border-yellow-300 bg-gradient-to-r from-yellow-50 to-amber-50 hover:border-yellow-400 hover:shadow-md' 
                      : 'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                  }`}
                >
                  <IconComponent className={`w-5 h-5 ${fieldType.premium ? 'text-yellow-600' : 'text-gray-600'}`} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900">
                        {fieldType.name}
                      </span>
                      {fieldType.premium && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-bold bg-gradient-to-r from-yellow-400 to-amber-500 text-white shadow-sm">
                          ⭐ PREMIUM
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500">
                      {fieldType.description}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Lista de campos creados */}
        <div className="flex-1 overflow-y-auto p-4">
          {fields.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-400 mb-2">
                <Type className="w-8 h-8 mx-auto" />
              </div>
              <p className="text-sm text-gray-600">
                No hay campos creados
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Usa los botones de arriba para agregar campos
              </p>
            </div>
          ) : (
            <div className="space-y-2">
              {fields.map((field, index) => {
                const fieldType = FIELD_TYPES.find(ft => ft.id === field.type);
                const IconComponent = fieldType?.icon || Type;
                
                return (
                  <div
                    key={field.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedField === field.id
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                    onClick={() => setSelectedField(field.id)}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <GripVertical className="w-4 h-4 text-gray-400" />
                      <IconComponent className="w-4 h-4 text-gray-600" />
                      <span className="text-sm font-medium text-gray-900 flex-1">
                        {field.label || 'Campo sin título'}
                      </span>
                      {field.required && (
                        <span className="text-xs text-red-500">*</span>
                      )}
                    </div>
                    
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-gray-500">
                        {fieldType?.name}
                      </span>
                      <div className="flex items-center gap-1">
                        <div className="flex flex-col gap-1 mr-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveField(field.id, 'up');
                            }}
                            disabled={index === 0}
                            className="p-1 bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-800 rounded transition-all shadow-sm disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Mover arriba"
                          >
                            <ArrowUp className="w-5 h-5" />
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              moveField(field.id, 'down');
                            }}
                            disabled={index === fields.length - 1}
                            className="p-1 bg-blue-50 hover:bg-blue-100 text-blue-600 hover:text-blue-800 rounded transition-all shadow-sm disabled:opacity-30 disabled:cursor-not-allowed"
                            title="Mover abajo"
                          >
                            <ArrowDown className="w-5 h-5" />
                          </button>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            duplicateField(field.id);
                          }}
                          className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          title="Duplicar campo"
                        >
                          <Copy className="w-3 h-3" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeField(field.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-600 rounded"
                          title="Eliminar campo"
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Panel central - Configuración del campo seleccionado */}
      <div className="w-1/3 bg-white border-r border-gray-200">
        {selectedFieldData ? (
          <FieldConfigPanel
            field={selectedFieldData}
            onUpdate={(updates) => updateField(selectedFieldData.id, updates)}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <Settings className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">
                Selecciona un campo para configurar
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Panel derecho - Vista previa */}
      <div className="w-1/3 bg-gray-50">
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Vista Previa</h3>
            <button
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-2 px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
            >
              <Eye className="w-4 h-4" />
              {showPreview ? 'Ocultar' : 'Mostrar'}
            </button>
          </div>
        </div>
        
        <div className="p-4 h-full overflow-y-auto">
          {showPreview && fields.length > 0 ? (
            <FieldPreview fields={fields} />
          ) : (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Eye className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">
                  {fields.length === 0 
                    ? 'Agrega campos para ver la vista previa'
                    : 'Haz clic en "Mostrar" para ver la vista previa'
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Componente para configurar un campo individual
const FieldConfigPanel = ({ field, onUpdate }) => {
  const fieldType = FIELD_TYPES.find(ft => ft.id === field.type);
  const hasOptions = ['select', 'radio', 'checkbox'].includes(field.type);

  // Normalizar field para asegurar que siempre tenga la estructura completa
  // Esto es crítico para campos antiguos de BD que no tienen validation o premiumConfig
  const normalizedField = React.useMemo(() => ({
    ...field,
    validation: field.validation || {},
    premiumConfig: field.premiumConfig || {},
    description: field.description || '',
    placeholder: field.placeholder || '',
    options: field.options || []
  }), [field]);

  // Estado local para inputs que requieren procesamiento (separación por comas)
  const [rowLabelsInput, setRowLabelsInput] = React.useState((normalizedField.premiumConfig?.rowLabels || []).join(', '));
  const [colLabelsInput, setColLabelsInput] = React.useState((normalizedField.premiumConfig?.columnLabels || []).join(', '));
  const [tableColumnsInput, setTableColumnsInput] = React.useState((normalizedField.premiumConfig?.tableColumns || []).join(', '));

  // Sincronizar estado local cuando cambia el field (importante para cuando se selecciona otro campo)
  React.useEffect(() => {
    setRowLabelsInput((normalizedField.premiumConfig?.rowLabels || []).join(', '));
    setColLabelsInput((normalizedField.premiumConfig?.columnLabels || []).join(', '));
    setTableColumnsInput((normalizedField.premiumConfig?.tableColumns || []).join(', '));
  }, [normalizedField.id]); // Se ejecuta cuando cambia el campo seleccionado

  const updateValidation = (key, value) => {
    onUpdate({
      validation: {
        ...normalizedField.validation,
        [key]: value
      }
    });
  };

  const updateOption = (index, value) => {
    const newOptions = [...normalizedField.options];
    newOptions[index] = value;
    onUpdate({ options: newOptions });
  };

  const addOption = () => {
    onUpdate({
      options: [...normalizedField.options, `Opción ${normalizedField.options.length + 1}`]
    });
  };

  const removeOption = (index) => {
    const newOptions = normalizedField.options.filter((_, i) => i !== index);
    onUpdate({ options: newOptions });
  };

  return (
    <div className="p-4 h-full overflow-y-auto">
      <div className="flex items-center gap-2 mb-6">
        {fieldType && <fieldType.icon className="w-5 h-5 text-blue-600" />}
        <h3 className="text-lg font-semibold text-gray-900">
          Configurar {fieldType?.name}
        </h3>
      </div>

      <div className="space-y-6">
        {/* Información básica */}
        <div>
          <h4 className="text-sm font-medium text-gray-900 mb-3">Información Básica</h4>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Etiqueta *
              </label>
              <input
                type="text"
                value={normalizedField.label}
                onChange={(e) => onUpdate({ label: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Escribe la etiqueta del campo"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Descripción (opcional)
                <span className="text-xs text-gray-500 ml-2">Soporta Markdown</span>
              </label>
              <textarea
                value={normalizedField.description}
                onChange={(e) => onUpdate({ description: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Descripción o instrucciones adicionales (usa **negrita**, - viñetas, etc.)"
              />
              <p className="text-xs text-gray-500 mt-1">
                💡 Usa: **negrita**, *cursiva*, - viñetas, 1. numeradas
              </p>
            </div>

            {/* Placeholder solo para campos NO premium */}
            {normalizedField.type !== 'checkbox' && 
             normalizedField.type !== 'matrix' && 
             normalizedField.type !== 'table' && 
             normalizedField.type !== 'ranking' && 
             normalizedField.type !== 'section_step' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Placeholder
                </label>
                <input
                  type="text"
                  value={normalizedField.placeholder}
                  onChange={(e) => onUpdate({ placeholder: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Texto de ejemplo para el usuario"
                />
              </div>
            )}

            <div className="flex items-center">
              <input
                type="checkbox"
                id={`required_${normalizedField.id}`}
                checked={field.required}
                onChange={(e) => onUpdate({ required: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor={`required_${field.id}`} className="ml-2 block text-sm text-gray-900">
                Campo obligatorio
              </label>
            </div>
          </div>
        </div>

        {/* Opciones para campos de selección */}
        {hasOptions && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Opciones</h4>
            
            <div className="space-y-2">
              {normalizedField.options.map((option, index) => (
                <div key={index} className="flex items-center gap-2">
                  <input
                    type="text"
                    value={option}
                    onChange={(e) => updateOption(index, e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder={`Opción ${index + 1}`}
                  />
                  <button
                    onClick={() => removeOption(index)}
                    className="p-2 text-red-500 hover:text-red-700 rounded"
                    disabled={normalizedField.options.length <= 1}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              
              <button
                onClick={addOption}
                className="w-full py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-gray-400 hover:text-gray-700"
              >
                + Agregar opción
              </button>
            </div>
          </div>
        )}

        {/* Validaciones - Solo para campos NO premium */}
        {normalizedField.type !== 'matrix' && 
         normalizedField.type !== 'table' && 
         normalizedField.type !== 'ranking' && 
         normalizedField.type !== 'section_step' && (
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">Validaciones</h4>
            
            <div className="space-y-4">
            {(normalizedField.type === 'text' || normalizedField.type === 'textarea') && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Mín. caracteres
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={normalizedField.validation.minLength || ''}
                      onChange={(e) => updateValidation('minLength', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Máx. caracteres
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={normalizedField.validation.maxLength || ''}
                      onChange={(e) => updateValidation('maxLength', e.target.value ? parseInt(e.target.value) : null)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Formato de validación (opcional)
                  </label>
                  <select
                    value={
                      normalizedField.validation.pattern === '^[a-zA-Z\\s]+$' ? 'letters' :
                      normalizedField.validation.pattern === '^\\d+$' ? 'numbers' :
                      normalizedField.validation.pattern === '^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$' ? 'email' :
                      normalizedField.validation.pattern === '^[0-9]{8}$' ? 'dni' :
                      normalizedField.validation.pattern === '^[0-9]{9}$' ? 'ruc' :
                      normalizedField.validation.pattern === '^\\+?[0-9]{9,15}$' ? 'phone' :
                      normalizedField.validation.pattern ? 'custom' : 'none'
                    }
                    onChange={(e) => {
                      const presets = {
                        none: '',
                        letters: '^[a-zA-Z\\s]+$',
                        numbers: '^\\d+$',
                        email: '^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$',
                        dni: '^[0-9]{8}$',
                        ruc: '^[0-9]{9}$',
                        phone: '^\\+?[0-9]{9,15}$',
                        custom: normalizedField.validation.pattern || ''
                      };
                      updateValidation('pattern', presets[e.target.value]);
                      updateValidation('patternType', e.target.value);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="none">Sin validación de formato</option>
                    <option value="letters">Solo letras y espacios</option>
                    <option value="numbers">Solo números</option>
                    <option value="email">Correo electrónico</option>
                    <option value="dni">DNI (8 dígitos)</option>
                    <option value="ruc">RUC (9 dígitos)</option>
                    <option value="phone">Teléfono (9-15 dígitos)</option>
                    <option value="custom">Patrón personalizado...</option>
                  </select>
                  
                  {/* Mostrar input personalizado si seleccionó "custom" */}
                  {(normalizedField.validation.patternType === 'custom' || 
                    (normalizedField.validation.pattern && 
                     normalizedField.validation.pattern !== '' &&
                     normalizedField.validation.pattern !== '^[a-zA-Z\\s]+$' &&
                     normalizedField.validation.pattern !== '^\\d+$' &&
                     normalizedField.validation.pattern !== '^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$' &&
                     normalizedField.validation.pattern !== '^[0-9]{8}$' &&
                     normalizedField.validation.pattern !== '^[0-9]{9}$' &&
                     normalizedField.validation.pattern !== '^\\+?[0-9]{9,15}$')) && (
                    <input
                      type="text"
                      value={normalizedField.validation.pattern}
                      onChange={(e) => updateValidation('pattern', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 mt-2 font-mono text-sm"
                      placeholder="Ej: ^[a-zA-Z0-9]+$"
                    />
                  )}
                  
                  {/* Hint informativo */}
                  {normalizedField.validation.pattern && (
                    <p className="text-xs text-gray-500 mt-1">
                      {normalizedField.validation.pattern === '^[a-zA-Z\\s]+$' && '✓ Solo acepta letras y espacios'}
                      {normalizedField.validation.pattern === '^\\d+$' && '✓ Solo acepta números'}
                      {normalizedField.validation.pattern === '^[a-zA-Z0-9._%-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$' && '✓ Valida formato de email'}
                      {normalizedField.validation.pattern === '^[0-9]{8}$' && '✓ Valida DNI de 8 dígitos'}
                      {normalizedField.validation.pattern === '^[0-9]{9}$' && '✓ Valida RUC de 9 dígitos'}
                      {normalizedField.validation.pattern === '^\\+?[0-9]{9,15}$' && '✓ Valida teléfono (opcional +)'}
                    </p>
                  )}
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Mensaje de error personalizado
              </label>
              <input
                type="text"
                value={normalizedField.validation.customMessage}
                onChange={(e) => updateValidation('customMessage', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Mensaje que se mostrará si hay errores"
              />
            </div>
          </div>
        </div>
        )}

        {/* 🌟 CONFIGURACIONES PREMIUM */}
        {normalizedField.type === 'matrix' && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-bold text-yellow-800">⭐ Configuración Matriz Premium</span>
            </div>
            
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Filas</label>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={normalizedField.premiumConfig?.rows || 3}
                    onChange={(e) => onUpdate({ 
                      premiumConfig: { ...normalizedField.premiumConfig, rows: parseInt(e.target.value) } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Columnas</label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={normalizedField.premiumConfig?.columns || 3}
                    onChange={(e) => onUpdate({ 
                      premiumConfig: { ...normalizedField.premiumConfig, columns: parseInt(e.target.value) } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Etiquetas de filas (separadas por coma)
                </label>
                <input
                  type="text"
                  value={rowLabelsInput}
                  onChange={(e) => setRowLabelsInput(e.target.value)}
                  onBlur={(e) => {
                    const labels = e.target.value.split(',').map(l => l.trim()).filter(l => l);
                    onUpdate({ 
                      premiumConfig: { 
                        ...normalizedField.premiumConfig, 
                        rowLabels: labels
                      } 
                    });
                  }}
                  placeholder="Ej: Criterio A, Criterio B, Criterio C"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {normalizedField.premiumConfig?.rowLabels?.length || 0} de {normalizedField.premiumConfig?.rows || 3} etiquetas
                </p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">
                  Etiquetas de columnas (separadas por coma)
                </label>
                <input
                  type="text"
                  value={colLabelsInput}
                  onChange={(e) => setColLabelsInput(e.target.value)}
                  onBlur={(e) => {
                    const labels = e.target.value.split(',').map(l => l.trim()).filter(l => l);
                    onUpdate({ 
                      premiumConfig: { 
                        ...normalizedField.premiumConfig, 
                        columnLabels: labels
                      } 
                    });
                  }}
                  placeholder="Ej: Nunca, A veces, Siempre"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {normalizedField.premiumConfig?.columnLabels?.length || 0} de {normalizedField.premiumConfig?.columns || 3} etiquetas
                </p>
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Tipo de celda</label>
                <select
                  value={normalizedField.premiumConfig?.cellType || 'text'}
                  onChange={(e) => onUpdate({ 
                    premiumConfig: { ...normalizedField.premiumConfig, cellType: e.target.value } 
                  })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                >
                  <option value="text">Texto</option>
                  <option value="number">Número</option>
                  <option value="checkbox">Checkbox</option>
                </select>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={normalizedField.premiumConfig?.allowEmpty || false}
                  onChange={(e) => onUpdate({ 
                    premiumConfig: { ...normalizedField.premiumConfig, allowEmpty: e.target.checked } 
                  })}
                  className="h-4 w-4 text-yellow-600"
                />
                <label className="ml-2 text-xs text-gray-700">Permitir celdas vacías</label>
              </div>
            </div>
          </div>
        )}

        {normalizedField.type === 'table' && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-bold text-yellow-800">⭐ Configuración Tabla Premium</span>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Columnas (separadas por coma)</label>
                <input
                  type="text"
                  value={tableColumnsInput}
                  onChange={(e) => setTableColumnsInput(e.target.value)}
                  onBlur={(e) => {
                    const columns = e.target.value.split(',').map(c => c.trim()).filter(c => c);
                    onUpdate({ 
                      premiumConfig: { 
                        ...field.premiumConfig, 
                        tableColumns: columns
                      } 
                    });
                  }}
                  placeholder="Ej: Tarea, Responsable, Fecha"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Filas mínimas</label>
                  <input
                    type="number"
                    min="1"
                    value={normalizedField.premiumConfig?.minRows || 1}
                    onChange={(e) => onUpdate({ 
                      premiumConfig: { ...normalizedField.premiumConfig, minRows: parseInt(e.target.value) } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Filas máximas</label>
                  <input
                    type="number"
                    min="1"
                    max="50"
                    value={normalizedField.premiumConfig?.maxRows || 10}
                    onChange={(e) => onUpdate({ 
                      premiumConfig: { ...normalizedField.premiumConfig, maxRows: parseInt(e.target.value) } 
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={normalizedField.premiumConfig?.rowAddable !== false}
                  onChange={(e) => onUpdate({ 
                    premiumConfig: { ...normalizedField.premiumConfig, rowAddable: e.target.checked } 
                  })}
                  className="h-4 w-4 text-yellow-600"
                />
                <label className="ml-2 text-xs text-gray-700">Permitir agregar filas</label>
              </div>
            </div>
          </div>
        )}

        {normalizedField.type === 'ranking' && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-bold text-yellow-800">⭐ Configuración Ranking Premium</span>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Ítems a ordenar (uno por línea)</label>
                <textarea
                  value={(normalizedField.premiumConfig?.items || []).join('\n')}
                  onChange={(e) => onUpdate({ 
                    premiumConfig: { 
                      ...normalizedField.premiumConfig, 
                      items: e.target.value.split('\n').filter(i => i.trim()) 
                    } 
                  })}
                  rows={5}
                  placeholder="Problema A&#10;Problema B&#10;Problema C"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono"
                />
              </div>
              
              <div className="flex items-center">
                <input
                  type="checkbox"
                  checked={normalizedField.premiumConfig?.allowTies || false}
                  onChange={(e) => onUpdate({ 
                    premiumConfig: { ...normalizedField.premiumConfig, allowTies: e.target.checked } 
                  })}
                  className="h-4 w-4 text-yellow-600"
                />
                <label className="ml-2 text-xs text-gray-700">Permitir empates</label>
              </div>
            </div>
          </div>
        )}

        {normalizedField.type === 'section_step' && (
          <div className="bg-gradient-to-r from-yellow-50 to-amber-50 border border-yellow-300 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-sm font-bold text-yellow-800">⭐ Configuración Sección Premium</span>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Título de la sección</label>
                <input
                  type="text"
                  value={normalizedField.premiumConfig?.sectionTitle || ''}
                  onChange={(e) => onUpdate({ 
                    premiumConfig: { ...normalizedField.premiumConfig, sectionTitle: e.target.value } 
                  })}
                  placeholder="Ej: Paso 1: Definición del contexto"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Descripción / Instrucciones</label>
                <textarea
                  value={normalizedField.premiumConfig?.sectionDescription || ''}
                  onChange={(e) => onUpdate({ 
                    premiumConfig: { ...normalizedField.premiumConfig, sectionDescription: e.target.value } 
                  })}
                  rows={3}
                  placeholder="Instrucciones para esta sección..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={field.premiumConfig?.allowCollapse !== false}
                    onChange={(e) => onUpdate({ 
                      premiumConfig: { ...field.premiumConfig, allowCollapse: e.target.checked } 
                    })}
                    className="h-4 w-4 text-yellow-600"
                  />
                  <label className="ml-2 text-xs text-gray-700">Permitir colapsar</label>
                </div>
                
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    checked={field.premiumConfig?.showNumber !== false}
                    onChange={(e) => onUpdate({ 
                      premiumConfig: { ...field.premiumConfig, showNumber: e.target.checked } 
                    })}
                    className="h-4 w-4 text-yellow-600"
                  />
                  <label className="ml-2 text-xs text-gray-700">Mostrar numeración</label>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Componente para mostrar vista previa del formulario
const FieldPreview = ({ fields }) => {
  const [formData, setFormData] = useState({});

  const handleFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-sm border">
      <h4 className="text-lg font-medium text-gray-900 mb-6">Vista Previa del Formulario</h4>
      
      {fields.length === 0 ? (
        <p className="text-gray-500 text-center py-8">
          No hay campos para mostrar
        </p>
      ) : (
        <form className="space-y-6">
          {fields.map((field) => (
            <FieldPreviewItem
              key={field.id}
              field={field}
              value={formData[field.id] || ''}
              onChange={(value) => handleFieldChange(field.id, value)}
            />
          ))}
          
          <div className="pt-4 border-t border-gray-200">
            <button
              type="button"
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg font-medium hover:bg-blue-700 transition-colors"
            >
              Enviar (Vista Previa)
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

// Componente para renderizar un campo individual en la vista previa
const FieldPreviewItem = ({ field, value, onChange }) => {
  // Normalizar campo para prevenir errores con campos legacy
  const normalizedField = React.useMemo(() => ({
    ...field,
    validation: field.validation || {},
    premiumConfig: field.premiumConfig || {},
    description: field.description || '',
    placeholder: field.placeholder || '',
    options: field.options || []
  }), [field]);

  // Función helper para normalizar opciones a formato consistente
  const normalizeOptions = (options) => {
    if (!Array.isArray(options)) return [];
    
    return options.map(option => {
      if (typeof option === 'string') {
        return { label: option, value: option };
      }
      if (typeof option === 'object' && option !== null && option.label && option.value) {
        return { label: String(option.label), value: option.value };
      }
      // Fallback para otros tipos
      return { label: String(option), value: String(option) };
    });
  };

  const normalizedOptions = normalizeOptions(normalizedField.options);

  const renderField = () => {
    switch (field.type) {
      case 'text':
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={normalizedField.placeholder}
            required={normalizedField.required}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );

      case 'textarea':
        return (
          <textarea
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={normalizedField.placeholder}
            required={normalizedField.required}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
        );

      case 'select':
        return (
          <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            required={normalizedField.required}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            <option value="">Selecciona una opción</option>
            {normalizedOptions.map((option, index) => (
              <option key={index} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        );

      case 'radio':
        return (
          <div className="space-y-2">
            {normalizedOptions.map((option, index) => (
              <label key={index} className="flex items-center gap-2">
                <input
                  type="radio"
                  name={normalizedField.id}
                  value={option.value}
                  checked={value === option.value}
                  onChange={(e) => onChange(e.target.value)}
                  required={normalizedField.required}
                  className="text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-900">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'checkbox':
        return (
          <div className="space-y-2">
            {normalizedOptions.map((option, index) => (
              <label key={index} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  value={option.value}
                  checked={Array.isArray(value) && value.includes(option.value)}
                  onChange={(e) => {
                    const currentArray = Array.isArray(value) ? value : [];
                    if (e.target.checked) {
                      onChange([...currentArray, option.value]);
                    } else {
                      onChange(currentArray.filter(v => v !== option.value));
                    }
                  }}
                  className="text-blue-600 focus:ring-blue-500 rounded"
                />
                <span className="text-sm text-gray-900">{option.label}</span>
              </label>
            ))}
          </div>
        );

      case 'matrix':
        const matrixRows = normalizedField.premiumConfig?.rows || 3;
        const matrixCols = normalizedField.premiumConfig?.columns || 3;
        const rowLabels = normalizedField.premiumConfig?.rowLabels || [];
        const colLabels = normalizedField.premiumConfig?.columnLabels || [];
        const cellType = normalizedField.premiumConfig?.cellType || 'text';
        const matrixValue = value || {};
        
        return (
          <div className="border border-yellow-300 rounded-lg bg-yellow-50 p-3 overflow-x-auto">
            <span className="text-xs font-semibold text-yellow-800 px-2 py-1 bg-yellow-200 rounded mb-2 inline-block">⭐ PREMIUM</span>
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  <th className="border border-gray-300 bg-gray-200 p-1"></th>
                  {Array.from({ length: matrixCols }).map((_, c) => (
                    <th key={c} className="border border-gray-300 bg-gray-100 p-2 text-xs font-medium">
                      {colLabels[c] || `Col ${c+1}`}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: matrixRows }).map((_, r) => (
                  <tr key={r}>
                    <td className="border border-gray-300 bg-gray-100 p-2 text-xs font-medium whitespace-nowrap">
                      {rowLabels[r] || `Fila ${r+1}`}
                    </td>
                    {Array.from({ length: matrixCols }).map((_, c) => (
                      <td key={c} className="border border-gray-300 p-1 bg-white">
                        {cellType === 'checkbox' ? (
                          <input
                            type="checkbox"
                            className="w-4 h-4 mx-auto block"
                            disabled
                          />
                        ) : (
                          <input
                            type={cellType === 'number' ? 'number' : 'text'}
                            className="w-full px-2 py-1 text-xs border-0"
                            placeholder={normalizedField.placeholder || '...'}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );

      case 'table':
        const tableCols = normalizedField.premiumConfig?.tableColumns || ['Col 1', 'Col 2'];
        
        return (
          <div className="border border-yellow-300 rounded-lg bg-yellow-50 p-3 overflow-x-auto">
            <span className="text-xs font-semibold text-yellow-800 px-2 py-1 bg-yellow-200 rounded mb-2 inline-block">⭐ PREMIUM</span>
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr>
                  {tableCols.map((col, i) => (
                    <th key={i} className="border border-gray-300 bg-gray-100 p-2 text-xs font-medium whitespace-nowrap">{col}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr>
                  {tableCols.map((col, i) => (
                    <td key={i} className="border border-gray-300 p-1 bg-white">
                      <input 
                        type="text" 
                        className="w-full px-2 py-1 text-xs border-0" 
                        placeholder={normalizedField.placeholder || `${col}...`}
                      />
                    </td>
                  ))}
                </tr>
              </tbody>
            </table>
          </div>
        );

      case 'ranking':
        const rankItems = normalizedField.premiumConfig?.items || ['Item 1', 'Item 2', 'Item 3'];
        
        return (
          <div className="border border-yellow-300 rounded-lg bg-yellow-50 p-3">
            <span className="text-xs font-semibold text-yellow-800 px-2 py-1 bg-yellow-200 rounded mb-2 inline-block">⭐ PREMIUM</span>
            <div className="space-y-1">
              {rankItems.map((item, i) => (
                <div key={i} className="flex items-center gap-2 bg-white p-2 rounded border text-sm">
                  <span className="font-bold text-yellow-600">{i+1}.</span>
                  <span>{item}</span>
                </div>
              ))}
            </div>
          </div>
        );

      case 'section_step':
        const sectionTitle = normalizedField.premiumConfig?.sectionTitle || 'Sección';
        const sectionDesc = normalizedField.premiumConfig?.sectionDescription || '';
        
        return (
          <div className="border-l-4 border-yellow-400 bg-yellow-50 p-3 rounded-r">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-yellow-800 px-2 py-1 bg-yellow-200 rounded">⭐ PREMIUM</span>
              <h4 className="font-bold text-gray-900 text-sm">{sectionTitle}</h4>
            </div>
            {sectionDesc && <p className="text-xs text-gray-700 mt-1">{sectionDesc}</p>}
          </div>
        );

      default:
        return <div className="text-gray-500">Tipo de campo no soportado</div>;
    }
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {normalizedField.label}
        {normalizedField.required && <span className="text-red-500 ml-1">*</span>}
      </label>
      
      {normalizedField.description && (
        <p className="text-sm text-gray-600 mb-2">{normalizedField.description}</p>
      )}
      
      {renderField()}
    </div>
  );
};

export default EplFieldDesigner;